import { useEffect, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface AppDetail {
  readonly applicationId: string;
  readonly name: string;
  readonly status: string;
  readonly versionNo: number;
}

interface RoleRow {
  readonly roleId: string;
  readonly roleName: string;
}

/**
 * GR L28.05 — Publish (`/lcnc/apps/{id}/publish`). Publishes an application and
 * captures its access matrix into `lcnc_app_permissions`. Per the permanent
 * ADMIN DECIDES rule the screen never hardcodes who may use the app — the
 * publisher explicitly grants view / submit rights per role via the matrix.
 */
export function GrAppPublishPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const { id } = useParams<{ id: string }>();
  const [app, setApp] = useState<AppDetail | null>(null);
  const [roles, setRoles] = useState<RoleRow[]>([]);
  const [grants, setGrants] = useState<Record<string, { view: boolean; submit: boolean }>>({});
  const [published, setPublished] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      apiRequest<{ data: AppDetail }>(`/v1/lcnc/applications/${id}`, { token }),
      apiRequest<RoleRow[]>('/v1/roles', { token }),
    ])
      .then(([a, r]) => { if (alive) { setApp(a.data); setRoles(r); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, id]);

  function toggle(roleId: string, kind: 'view' | 'submit'): void {
    setGrants((prev) => {
      const cur = prev[roleId] ?? { view: false, submit: false };
      return { ...prev, [roleId]: { ...cur, [kind]: !cur[kind] } };
    });
  }

  async function publish(): Promise<void> {
    setSubmitting(true);
    try {
      const permissions = roles
        .map((r) => ({ roleId: r.roleId, ...(grants[r.roleId] ?? { view: false, submit: false }) }))
        .filter((p) => p.view || p.submit);
      await apiRequest(`/v1/lcnc/applications/${id}/publish`, {
        token, method: 'POST', body: { versionNo: app?.versionNo ?? 1, permissions },
      });
      setPublished(true);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main aria-label="lcnc-publish">
      {error && <p role="alert">{error}</p>}
      {published && <p aria-label="published">{t('gr28.publish.published')}</p>}
      {loading ? <p>{t('gr28.publish.loading')}</p> : app && (
        <>
          <h1 aria-label="app-heading">{t('gr28.publish.heading', { name: app.name, status: app.status })}</h1>
          <table aria-label="permission-matrix">
            <thead><tr><th>{t('gr28.publish.colRole')}</th><th>{t('gr28.publish.colView')}</th><th>{t('gr28.publish.colSubmit')}</th></tr></thead>
            <tbody>
              {roles.map((r) => (
                <tr key={r.roleId}>
                  <td>{r.roleName}</td>
                  <td>
                    <input aria-label={`view-${r.roleId}`} type="checkbox"
                      checked={grants[r.roleId]?.view ?? false} onChange={() => toggle(r.roleId, 'view')} />
                  </td>
                  <td>
                    <input aria-label={`submit-${r.roleId}`} type="checkbox"
                      checked={grants[r.roleId]?.submit ?? false} onChange={() => toggle(r.roleId, 'submit')} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button type="button" aria-label="publish-app" onClick={publish} disabled={submitting}>
            {submitting ? t('gr28.publish.submitting') : t('gr28.publish.submit')}
          </button>
        </>
      )}
    </main>
  );
}
